import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { ArrowIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import {
  FundraisingCapTableByStakeholderAll,
  LiquidityCapTableByStakeholderAll,
} from '../../../../hooks/scenario-modeling/index';
import { PageRoute } from '../../../../types/page.types';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { objectTypeCheck } from '../../../../utils/object-type-check.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import { useScenarioStore } from '../../scenario-modeling.store';
import { ItemCell } from './item-cell';

type StakeholderTypeItemProps = {
  item: FundraisingCapTableByStakeholderAll | LiquidityCapTableByStakeholderAll;
  name: string;
};

export const StakeholderTypeItem: FC<StakeholderTypeItemProps> = ({ item, name }) => {
  const {
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    shareClasses,
    votingPercentage,
    dilutedChange,
    dilutedPercentageChange,
    investmentChange,
    issuedChange,
    issuedPercentageChange,
    votingPercentageChange,
    stakeholders,
  } = item;

  const { scenarioId } = useScenarioStore();
  const navigate = useNavigate();

  const isLiquidity = objectTypeCheck<LiquidityCapTableByStakeholderAll>(item, 'seniorityColumns');

  let liquidityProceeds = 0;
  let roi = 0;

  if (isLiquidity) {
    liquidityProceeds = item.minLiquidityProceeds;
    roi = item.roi;
  }
  return (
    <>
      <div className="flex w-full flex-col gap-2 rounded-md pt-3 shadow-sm">
        <div className="flex w-full items-center gap-4 border-b-[2px] border-b-gray-100 px-3 pb-3">
          <span className="max-w-full truncate text-base font-semibold text-gray-700">{name}</span>
        </div>
        <div className="grid grid-flow-row grid-cols-2">
          <ItemCell
            change={issuedPercentageChange}
            title={<AppFormattedMessage id="ISSUED_PERCENTAGE" />}
            value={toRound(toNumber(issuedPercentage) * 100)}
          />
          <ItemCell
            change={dilutedPercentageChange}
            title={<AppFormattedMessage id="DILUTED_PERCENTAGE" />}
            value={toRound(toNumber(dilutedPercentage) * 100)}
          />
          <ItemCell
            change={votingPercentageChange}
            title={<AppFormattedMessage id="VOTING_PERCENTAGE" />}
            value={toRound(toNumber(votingPercentage) * 100)}
          />
          <ItemCell
            change={issuedChange}
            title={<AppFormattedMessage id="NO_ISSUED" />}
            value={issued}
          />
          <ItemCell
            change={dilutedChange}
            title={<AppFormattedMessage id="DILUTED_SHARES" />}
            value={diluted}
          />
          <ItemCell
            change={investmentChange}
            title={
              <>
                <AppFormattedMessage id="INVESTMENT" /> (<SelectedCompanyCurrency />)
              </>
            }
            value={investment}
          />{' '}
          {isLiquidity && (
            <>
              <ItemCell
                title={<AppFormattedMessage id="LIQUIDITY_PROCEEDS" />}
                value={toNumber(liquidityProceeds)}
              />
              <ItemCell title={<AppFormattedMessage id="ROI" />} value={toMathRound(roi)} />
            </>
          )}
        </div>
        {shareClasses && shareClasses?.length > 0 && (
          <div className="grid grid-flow-row grid-cols-2">
            {shareClasses.map(({ name, issued, issuedChange, id }) => (
              <ItemCell change={issuedChange} key={id} title={name} value={issued} />
            ))}
          </div>
        )}
        {stakeholders && stakeholders?.length > 0 && (
          <Button
            className="flex w-full items-center justify-between rounded-none bg-brand-25 px-4 py-3"
            onClick={() =>
              navigate(`${PageRoute.SCENARIO_MODELING}/${scenarioId}/${name}`, {
                state: { stakeholders },
              })
            }
            styleType="NONE"
          >
            <span className="text-sm font-[450] text-brand-700">
              <AppFormattedMessage
                id="VIEW_ASSOCIATED_STAKEHOLDERS"
                values={{
                  stakeholdersCount: stakeholders?.length,
                }}
              />
            </span>
            <ArrowIcon className="rotate-180" iconColor="#2565C8" />
          </Button>
        )}
      </div>
    </>
  );
};
